/**
 * Calendar panel wrapper — switches between Manual and SelfSchedule calendar modes.
 * Fetches interviewer calendar events and passes them as external events.
 * Exposes CalendarHandle via forwardRef for programmatic navigation (goToDate).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import moment from 'moment-timezone';
import {
  useInterviewerCalendarChunksQuery,
  CalendarEventsFetchError,
  type PrefetchWeeks,
} from '@/api/calendarEvents';
import { useShowToast } from '@/hooks';
import type { CalendarHandle } from '@kula-ai/components';
import type { ScheduleMode } from '../../schedule.types';
import type { ScheduleSlot } from '../details/useScheduleSharedState';
import type { ManualCalendarExternalEvent } from './hooks';
import { getPastelSlotColorsForUser } from './interviewerPastelColors';
import { ManualCalendar } from './ManualCalendar';
import { SelfScheduleCalendar } from './SelfScheduleCalendar';
import { syncScheduleInterviewerPastelKeys } from '../scheduleInterviewerPastelMap';

interface CalendarPanelProps {
  mode: ScheduleMode;
  duration: number;
  timezone?: string;
  interviewerIds?: string[];
  meetingRoomIds?: string[];
  interviewName?: string;
  candidateName?: string;
  bookedSlot?: ScheduleSlot | null;
  onSlotSelect?: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
  isEdit?: boolean;
  /** How many ISO weeks to load from the visible week anchor (default 2) */
  prefetchWeeks?: PrefetchWeeks;
  /** Extra weeks beyond `prefetchWeeks` to background-prefetch (next+next navigation) */
  lookaheadPrefetchWeeks?: number;
  /** Calendar events API — forwarded as `ats_job_id` when set */
  atsJobId?: number;
}

function fallbackVisibleWeekStartIso(tz: string): string {
  return moment().tz(tz).startOf('isoWeek').startOf('day').toISOString();
}

export const CalendarPanel = forwardRef<CalendarHandle, CalendarPanelProps>(
  (
    {
      mode,
      duration,
      timezone,
      interviewerIds = [],
      meetingRoomIds = [],
      interviewName,
      candidateName,
      bookedSlot,
      onSlotSelect,
      onTimezoneChange,
      onSlotsChange,
      isEdit,
      prefetchWeeks = 2,
      lookaheadPrefetchWeeks = 1,
      atsJobId,
    },
    ref
  ) => {
    const toast = useShowToast();
    const isSelfSchedule =
      mode === 'self-schedule' || mode === 'self-schedule-adhoc';

    const resolvedTz =
      timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isLocalTimezone = resolvedTz === 'local';

    const [activeVisibleStartIso, setActiveVisibleStartIso] = useState<
      string | null
    >(null);

    const handleActiveRangeChange = useCallback(
      (range: { start: string; end: string }) => {
        const startIso = moment
          .tz(range.start, resolvedTz)
          .startOf('day')
          .toISOString();
        setActiveVisibleStartIso(startIso);
      },
      [resolvedTz]
    );

    const { visibleWeekStartIso } = useMemo(() => {
      const tz = resolvedTz;
      const visibleStart =
        activeVisibleStartIso ?? fallbackVisibleWeekStartIso(tz);
      const startM = moment(visibleStart).tz(tz).startOf('day');
      return {
        visibleWeekStartIso: startM.toISOString(),
      };
    }, [activeVisibleStartIso, resolvedTz]);

    const visibleWeekEndIso = useMemo(
      () =>
        moment(visibleWeekStartIso).tz(resolvedTz).add(7, 'days').toISOString(),
      [resolvedTz, visibleWeekStartIso]
    );

    const numericInterviewerIds = useMemo(
      () =>
        interviewerIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      [interviewerIds]
    );

    const sortedInterviewerIds = useMemo(
      () => [...numericInterviewerIds].sort((a, b) => a - b),
      [numericInterviewerIds]
    );

    const {
      mergedEvents: calendarEvents,
      isBlockingBusyFetch,
      fetchError,
    } = useInterviewerCalendarChunksQuery({
      userIds: numericInterviewerIds,
      anchorWeekStartIso: visibleWeekStartIso,
      timezone: resolvedTz,
      prefetchWeeks,
      lookaheadPrefetchWeeks,
      ...(atsJobId !== null && atsJobId > 0 ? { atsJobId } : {}),
      enabled: !isSelfSchedule && numericInterviewerIds.length > 0,
      isLocalTimezone,
    });

    const lastToastKeyRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        !fetchError ||
        fetchError.name === 'AbortError' ||
        fetchError.name === 'CanceledError'
      ) {
        return;
      }
      const key = `${fetchError.name}:${fetchError.message}`;
      if (lastToastKeyRef.current === key) {
        return;
      }
      lastToastKeyRef.current = key;
      const description =
        fetchError instanceof CalendarEventsFetchError
          ? fetchError.userMessage
          : fetchError.message;
      toast({
        title: 'Calendar',
        description,
        status: 'error',
        duration: 8000,
      });
    }, [fetchError, toast]);

    const calendarEventsForVisibleWeek = useMemo(() => {
      const vs = moment(visibleWeekStartIso).valueOf();
      const ve = moment(visibleWeekEndIso).valueOf();
      return calendarEvents.filter((evt) => {
        const s = moment(evt.start).valueOf();
        const e = moment(evt.end).valueOf();
        if (!Number.isFinite(s) || !Number.isFinite(e)) {
          return false;
        }
        return s < ve && e > vs;
      });
    }, [calendarEvents, visibleWeekEndIso, visibleWeekStartIso]);

    const showBusyEventsLoader =
      !isSelfSchedule &&
      numericInterviewerIds.length > 0 &&
      !isLocalTimezone &&
      isBlockingBusyFetch;

    const externalEvents = useMemo((): ManualCalendarExternalEvent[] => {
      syncScheduleInterviewerPastelKeys(sortedInterviewerIds);
      const seen = new Set<string>();
      const rows: ManualCalendarExternalEvent[] = [];
      for (const evt of calendarEventsForVisibleWeek) {
        const uidRaw = evt.user_id;
        const uid =
          typeof uidRaw === 'number' && Number.isFinite(uidRaw)
            ? uidRaw
            : sortedInterviewerIds[0];
        if (
          typeof uid === 'number' &&
          Number.isFinite(uid) &&
          !sortedInterviewerIds.includes(uid)
        ) {
          continue;
        }
        const dedupeKey = `${evt.id ?? ''}|${evt.start}|${evt.end}|${uid ?? ''}`;
        if (seen.has(dedupeKey)) {
          continue;
        }
        seen.add(dedupeKey);
        const colors = getPastelSlotColorsForUser(sortedInterviewerIds, uid);
        rows.push({
          id: evt.id ?? `${evt.start}-${evt.end}-${uid ?? ''}`,
          start: evt.start,
          end: evt.end,
          title: evt.title ?? 'Busy',
          backgroundColor: colors.backgroundColor,
          /** KInterviewScheduler parity: border matches fill (`borderColor: light`) — no dark outline ring. */
          borderColor: colors.backgroundColor,
          textColor: colors.textColor,
        });
      }
      return rows;
    }, [calendarEventsForVisibleWeek, sortedInterviewerIds]);

    return (
      <Flex w="100%" h="100%" minH={0} flexDir="column" overflow="visible">
        {isSelfSchedule ? (
          <SelfScheduleCalendar
            duration={duration}
            timezone={timezone}
            interviewerIds={interviewerIds}
            meetingRoomIds={meetingRoomIds}
            onSlotsChange={onSlotsChange}
          />
        ) : (
          <Box position="relative" w="100%" h="100%" minH={0} flex="1">
            <ManualCalendar
              ref={ref}
              duration={duration}
              timezone={timezone}
              interviewName={interviewName}
              candidateName={candidateName}
              bookedSlot={bookedSlot}
              externalEvents={externalEvents}
              onSlotSelect={onSlotSelect ?? (() => {})}
              onTimezoneChange={onTimezoneChange}
              onActiveRangeChange={handleActiveRangeChange}
              isEdit={isEdit}
            />
            {showBusyEventsLoader ? (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="whiteAlpha.800"
                zIndex={20}
                pointerEvents="none"
                borderRadius="md"
              >
                <Spinner size="lg" color="secondary.500" thickness="3px" />
              </Flex>
            ) : null}
          </Box>
        )}
      </Flex>
    );
  }
);

CalendarPanel.displayName = 'CalendarPanel';
